import { Component, ComponentRef, OnInit } from '@angular/core';
import { AbstractControlComponent, SelectComponent } from '@fgpp-ui/components';
import { take } from 'rxjs';
import { FeeType } from '../../models/fee-type.model';
import { IValueChange } from '../../../core/interfaces/form-value-change.interface';
import { BFOnFormLoadComplete, BFOnValueChange, BFProfileOnSave } from '../../../core/interfaces/sdk-profile-hooks.interface';
import { ProfileFormTemplateProviders } from '../../../core/templates/profile-form.template.providers';
import { AbstractProfileFormComponent } from '../../abstract/abstract-profile/abstract-profile-form.component';
import { PayingPartyType } from './enums/paying-party-type.enum';
import { ProfileLayoutType } from '../../enums/profile-layout-type.enum';
import { Accounts } from '../../models/accounts.model';
import { TaxRateApplyOn } from './enums/tax-rate-apply-on.enum';


@Component({
    selector: 'app-fee-type',
    templateUrl: '../../../core/templates/profile-form.template.html',
    styleUrls: ['../../../core/templates/profile-form.template.scss'],
    providers: ProfileFormTemplateProviders
})
export class FeeTypeComponent extends AbstractProfileFormComponent implements BFOnFormLoadComplete, BFOnValueChange, BFProfileOnSave, OnInit {


    fgppFormLoadComplete(): void {
        this.updateProfileSubTitle();
        this.handleFeeAcountNumber(false);
        this.updateTaxControlsAvailability(false);
    }

    fgppValueChanges(change: IValueChange): void {
        switch (change.control) {
            case FeeType.PAYING_PARTY:
                this.handleFeeAcountNumber(true);
                break;
            case FeeType.FEES_SUSP_ACC_NO:
                this.updateFeeAccountControls();
                break;
            case FeeType.TAX_RATE:
                this.updateApplyOnControl(change.value.inputValue);
                this.updateTaxControlsAvailability(true);
                break;
            case FeeType.TAX_SUSP_ACC_NO:
                this.updateTaxAccountControls();
                break;

        }
    }

    fgppOnSave(): boolean {
        this.convertAmountControlsToString()
        return true;
    }

    updateProfileSubTitle(): void {
        const payingPartyControl = this.formMgr.get(FeeType.PAYING_PARTY);
        const value = payingPartyControl.getValue()
        if (value) {
            const selectComponent = payingPartyControl.getLayoutControl<SelectComponent>();
            selectComponent.dataLoaded.pipe(take(1)).subscribe(() => {
                this.canvasEvent.setTarget(this.formService.getId()).set('state', 'setValue').emit();
            })
        }
    }

    private handleFeeAcountNumber(payingPartyChanged: boolean): void {
        const payingParty = this.formMgr.get(FeeType.PAYING_PARTY);
        const feeAccountNumber = this.formMgr.get(FeeType.FEES_SUSP_ACC_NO);
        if (payingParty.getValue() === PayingPartyType.AGENT_FEE) {
            if (payingPartyChanged) {
                feeAccountNumber.markAsUntouched().setValue('');
                feeAccountNumber.markAsUntouched().setValue('');
                this.updateFeeAccountControls()
            }
            feeAccountNumber.markAsOptional().disable();
            feeAccountNumber.markAsOptional().disable();

        } else {
            feeAccountNumber.markAsRequired();
            if (this.layoutMode !== ProfileLayoutType.VIEW) {
                feeAccountNumber.enable();
            }
        }
    }

    private updateFeeAccountControls(): void {
        this.updateAccountControls(FeeType.FEES_SUSP_ACC_NO, FeeType.FEES_SUSP_ACC_CCY, FeeType.FEES_SUSP_ACC_OFFICE);
    }

    private updateTaxAccountControls(): void {
        this.updateAccountControls(FeeType.TAX_SUSP_ACC_NO, FeeType.TAX_SUSP_ACC_CCY, FeeType.TAX_SUSP_ACC_OFFICE);
    }

    private updateAccountControls(acountNumberControlId: FeeType, accountCurrencyControlId: FeeType, AccountOfficeControlId: FeeType): void {
        const data = this.getDrilldownData(acountNumberControlId);
        const acountNumber = this.formMgr.get(acountNumberControlId);

        if (data === null && acountNumber.getValue() && !acountNumber.getLayoutControl().controller.errors) {
            return;
        }
        const currency = data ? data[Accounts.CURRENCY] : '';
        const office = data ? data[Accounts.OFFICE] : '';
        this.formMgr.get(accountCurrencyControlId).setValue(currency);
        this.formMgr.get(AccountOfficeControlId).setValue(office);
    }

    private updateApplyOnControl(taxRate: number): void {
        const applyOn = taxRate || taxRate === 0 ? TaxRateApplyOn.PRINCIPLE : '';
        this.formMgr.get(FeeType.TAX_RATE_APPLY_ON).setValue(applyOn);
    }

    private updateTaxControlsAvailability(taxRateChanged: boolean): void {
        const taxRate = this.formMgr.get(FeeType.TAX_RATE).getValue();
        if (this.layoutMode !== ProfileLayoutType.VIEW) {
            if (taxRate || taxRate === 0) {
                this.formMgr.get(FeeType.TAX_ON_TAX_RATE).enable();
                this.formMgr.get(FeeType.TAX_ON_TAX_RATE_VALID_UNTIL).enable();
                this.formMgr.get(FeeType.NEW_TAX_ON_TAX_RATE).enable();

            } else {
                if (taxRateChanged) {
                    this.formMgr.get(FeeType.TAX_ON_TAX_RATE).markAsUntouched().setValue('');
                    this.formMgr.get(FeeType.TAX_ON_TAX_RATE_VALID_UNTIL).markAsUntouched().setValue('');
                    this.formMgr.get(FeeType.NEW_TAX_ON_TAX_RATE).markAsUntouched().setValue('');
                }
                this.formMgr.get(FeeType.TAX_ON_TAX_RATE).disable();
                this.formMgr.get(FeeType.TAX_ON_TAX_RATE_VALID_UNTIL).disable();
                this.formMgr.get(FeeType.NEW_TAX_ON_TAX_RATE).disable();
            }
        }
    }

    // When amount control is cleared by the user the control value is updated to null
    // The backend ignores null values as modified fields because it expects to get strings
    // The amount values should be converted to string before 'save' action
    private convertAmountControlsToString(): void {
        const amountControlNames = this.getControlNames('amount');
        amountControlNames.forEach((controlName: string) => {
            const value = this.formMgr.get(controlName).getValue();
            this.formMgr.get(controlName).setValue(value?.toString() || '');
        });
    }

    private getControlNames(controlType: string): Array<string> {
        const controlNames = [];
        this.componentManager.getStore().forEach((componentRef: ComponentRef<AbstractControlComponent>, controlName: string) => {
            const component = componentRef.instance;
            if (component.controlData.controlType === controlType) {
                controlNames.push(controlName);
            }
        });
        return controlNames;
    }
}
